import { useState } from "react";
import { FaMinus } from "react-icons/fa";
import { GoPlus } from "react-icons/go";
import { formatErrorMessageV2 } from "../utils";
import { useTranslation } from "react-i18next";

export type CounterInputV2Props = {
  name: string;
  label?: string;
  value?: string | number;
  min?: number;
  max?: number;
  suffix?: string;
  description?: string;
  allowDecimal?: boolean;
  disabled?: boolean;
  error?: string;
  onChange?: (value: number) => void;
};

export function CounterInputV2({
  name,
  label,
  value = 0,
  min = 0,
  max,
  suffix,
  description,
  allowDecimal = false,
  disabled = false,
  error,
  onChange,
}: CounterInputV2Props) {
  const { t } = useTranslation();
  const counterValue = normalizeCounterValue(value, min, max, allowDecimal);
  const [decimalInput, setDecimalInput] = useState<{ text: string; value: number } | null>(null);
  const inputValue = allowDecimal && decimalInput?.value === counterValue
    ? decimalInput.text
    : counterValue;

  function updateValue(nextValue: number) {
    setDecimalInput(null);
    onChange && onChange(normalizeCounterValue(nextValue, min, max, allowDecimal));
  }

  function handleInputChange(nextValue: string) {
    if (allowDecimal) {
      if (!/^\d*(?:[.,]\d*)?$/.test(nextValue)) return;
      const parsedValue = Number(nextValue.replace(",", "."));
      const nextNumber = normalizeCounterValue(parsedValue, min, max, true);
      setDecimalInput(Number.isFinite(parsedValue) && parsedValue === nextNumber
        ? { text: nextValue, value: nextNumber }
        : null);
      onChange?.(nextNumber);
      return;
    }
    const normalizedInput = nextValue.replace(/\D/g, "");
    updateValue(normalizedInput ? Number(normalizedInput) : min);
  }

  return (
    <div className="ov2-field">
      {label && <span className="ov2-field__label">{label}</span>}
      <span className={`ov2-counter ${error ? "ov2-counter--error" : ""} ${disabled ? "ov2-counter--disabled" : ""}`}>
        <button
          type="button"
          aria-label={t("commonUi.decreaseValue")}
          disabled={disabled || counterValue <= min}
          onClick={() => updateValue(counterValue - 1)}
        >
          <FaMinus />
        </button>
        <span className="ov2-counter__input">
          <input
            name={name}
            type="text"
            inputMode={allowDecimal ? "decimal" : "numeric"}
            pattern={allowDecimal ? "[0-9]*[.,]?[0-9]*" : "[0-9]*"}
            value={inputValue}
            style={{ width: `${Math.max(String(inputValue).length, 1)}ch` }}
            disabled={disabled}
            onBlur={() => setDecimalInput(null)}
            onChange={(event) => handleInputChange(event.target.value)}
          />
          {suffix && <span>{suffix}</span>}
        </span>
        <button
          type="button"
          aria-label={t("commonUi.increaseValue")}
          disabled={disabled || (max !== undefined && counterValue >= max)}
          onClick={() => updateValue(counterValue + 1)}
        >
          <GoPlus />
        </button>
      </span>
      {description && <small className="ov2-counter__description">{description}</small>}
      {error && <small className="ov2-field__error animation__fadeIn">{formatErrorMessageV2(error)}</small>}
    </div>
  );
}

function normalizeCounterValue(value: string | number | undefined, min = 0, max?: number, allowDecimal = false) {
  const parsedValue = Number(value || 0);
  const numberValue = allowDecimal ? parsedValue : Math.floor(parsedValue);
  const safeValue = Number.isFinite(numberValue) ? numberValue : min;
  const withMin = Math.max(safeValue, min);
  return max !== undefined ? Math.min(withMin, max) : withMin;
}
